import React, {useEffect, useRef, useState} from 'react';
import {Grid, Tag, Typography} from 'antd';
import {ClockCircleOutlined, FireOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import NewsService from '../../service/newsService';
import {ApiResponse, ClusterSummaryView} from '../../types/news';
import {useTheme} from '../ThemeProvider';
import {sanitizeHtml} from '../../utils/htmlUtils';

dayjs.extend(relativeTime);

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface BreakingNewsTickerProps {
    refreshInterval?: number; // in milliseconds, default 5 minutes
}

const BreakingNewsTicker: React.FC<BreakingNewsTickerProps> = ({ 
    refreshInterval = 5 * 60 * 1000 // 5 minutes
}) => {
    const [breakingNews, setBreakingNews] = useState<ClusterSummaryView[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [isPageVisible, setIsPageVisible] = useState<boolean>(true);
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const screens = useBreakpoint();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const tickerRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const fetchBreakingNews = async () => {
        try {
            const response: ApiResponse<ClusterSummaryView[]> = await NewsService.getBreakingNews();
            setBreakingNews(response.data);
            setError(null);
            if (response.data.length > 0) {
                setCurrentIndex(0); // Reset to first item when new data arrives
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load breaking news');
            setBreakingNews([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch breaking news on mount and set up refresh interval
    useEffect(() => {
        // Fetch initial data
        fetchBreakingNews();
        
        // Set up refresh interval only when page is visible
        if (isPageVisible) {
            intervalRef.current = setInterval(fetchBreakingNews, refreshInterval);
        } else if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [refreshInterval, isPageVisible]);

    // Handle page visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsPageVisible(!document.hidden);
        };

        const handleFocus = () => {
            setIsPageVisible(true);
        };

        const handleBlur = () => {
            setIsPageVisible(false);
        };

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);

        // Set initial state
        setIsPageVisible(!document.hidden);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    // Set up ticker rotation with smooth sliding animation
    useEffect(() => {
        if (breakingNews.length > 1 && !isHovered && isPageVisible) {
            tickerRef.current = setInterval(() => {
                setCurrentIndex((prevIndex) => (prevIndex + 1) % breakingNews.length);
            }, 8000); // Change item every 8 seconds (slower)
        } else if (tickerRef.current) {
            clearInterval(tickerRef.current);
            tickerRef.current = null;
        }

        return () => {
            if (tickerRef.current) {
                clearInterval(tickerRef.current);
            }
        };
    }, [breakingNews.length, isHovered, isPageVisible]);

    const handleTickerClick = (item: ClusterSummaryView) => {
        if (item.num_related_articles <= 1 && item.article_url) {
            window.open(item.article_url, '_blank', 'noopener,noreferrer');
        } else {
            navigate(`/news/cluster/${item.cluster_id}`);
        }
    };

    const handleMouseEnter = () => {
        setIsHovered(true);
        setShowPopup(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
        setShowPopup(false);
    };

    // Don't render if no breaking news or error
    if (loading || error || breakingNews.length === 0) {
        return null;
    }

    // Hide on mobile devices (screens smaller than md)
    if (!screens.md) {
        return null;
    }

    return (
        <>
            <div
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    background: isDark
                        ? 'linear-gradient(90deg, #722ed1 0%, #eb2f96 100%)'
                        : 'linear-gradient(90deg, #ff4d4f 0%, #ff7a45 100%)',
                    color: isDark ? '#ffffff' : '#ffffff',
                    padding: '12px 20px',
                    boxShadow: isDark 
                        ? '0 -4px 12px rgba(0,0,0,0.4)' 
                        : '0 -4px 12px rgba(0,0,0,0.15)',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    borderTop: isDark 
                        ? '2px solid rgba(255,255,255,0.3)' 
                        : '2px solid rgba(255,255,255,0.4)'
                }}
                onClick={() => handleTickerClick(breakingNews[currentIndex])}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%',
                    gap: '16px'
                }}>
                    {/* Breaking News Label */}
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        flexShrink: 0
                    }}>
                        <FireOutlined 
                            style={{ 
                                fontSize: '18px', 
                                animation: 'pulse 2s infinite',
                                color: isDark ? '#ffd700' : '#fff700'
                            }} 
                        />
                        <Text 
                            style={{ 
                                color: '#ffffff',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                textTransform: 'uppercase',
                                letterSpacing: '1px'
                            }}
                        >
                            BREAKING
                        </Text>
                    </div>

                    {/* News Content - Sliding Container */}
                    <div 
                        ref={containerRef}
                        style={{ 
                            flex: 1, 
                            overflow: 'hidden',
                            position: 'relative',
                            height: '28px',
                            marginLeft: '8px',
                            marginRight: '8px'
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                width: `${breakingNews.length * 100}%`,
                                transform: `translateX(-${currentIndex * (100 / breakingNews.length)}%)`,
                                transition: 'transform 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                height: '100%'
                            }}
                        >
                            {breakingNews.map((item, index) => (
                                <div
                                    key={item.cluster_id}
                                    style={{
                                        width: `${100 / breakingNews.length}%`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        paddingRight: '40px'
                                    }}
                                >
                                    <Text 
                                        style={{ 
                                            color: '#ffffff',
                                            fontSize: '15px',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '100%'
                                        }}
                                    >
                                        {item.last_breaking_headline || item.cluster_headline}
                                    </Text>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Rating and Time */}
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        flexShrink: 0
                    }}>
                        <Tag 
                            color={isDark ? "gold" : "orange"}
                            style={{ 
                                margin: 0,
                                fontWeight: 'bold',
                                border: 'none',
                                fontSize: '12px',
                                background: isDark ? '#faad14' : '#fa8c16',
                                color: '#000000'
                            }}
                        >
                            ⚡{breakingNews[currentIndex]?.last_breaking_rating || breakingNews[currentIndex]?.root_breaking_rating}
                        </Tag>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <ClockCircleOutlined style={{ 
                                fontSize: '12px', 
                                opacity: 0.8,
                                color: '#ffffff'
                            }} />
                            <Text 
                                style={{ 
                                    color: '#ffffff',
                                    fontSize: '11px',
                                    opacity: 0.9
                                }}
                            >
                                {breakingNews[currentIndex]?.last_breaking_update_datetime
                                    ? dayjs(breakingNews[currentIndex].last_breaking_update_datetime).fromNow()
                                    : 'Recently'}
                            </Text>
                        </div>

                        {/* Item Counter */}
                        {breakingNews.length > 1 && (
                            <Text 
                                style={{ 
                                    color: '#ffffff',
                                    fontSize: '10px',
                                    opacity: 0.9,
                                    minWidth: '35px',
                                    textAlign: 'center',
                                    background: isDark 
                                        ? 'rgba(255,255,255,0.15)' 
                                        : 'rgba(255,255,255,0.2)',
                                    padding: '2px 6px',
                                    borderRadius: '8px'
                                }}
                            >
                                {currentIndex + 1}/{breakingNews.length}
                            </Text>
                        )}
                    </div>
                </div>
            </div>

            {/* Hover Popup */}
            {showPopup && breakingNews[currentIndex] && (
                <div
                    className="breaking-news-popup"
                    style={{
                        position: 'fixed',
                        bottom: '60px',
                        left: '20px',
                        right: '20px',
                        zIndex: 1001,
                        background: isDark ? '#1f1f1f' : '#ffffff',
                        border: isDark ? '1px solid #434343' : '1px solid #d9d9d9',
                        borderRadius: '8px',
                        padding: '16px',
                        boxShadow: isDark 
                            ? '0 4px 20px rgba(0,0,0,0.6)' 
                            : '0 4px 20px rgba(0,0,0,0.15)',
                        maxWidth: '600px',
                        margin: '0 auto',
                        animation: 'slideUp 0.3s ease-out'
                    }}
                >
                    <div style={{ marginBottom: '8px' }}>
                        <Text 
                            style={{ 
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: isDark ? '#ffffff' : '#000000'
                            }}
                        >
                            {breakingNews[currentIndex].last_breaking_headline || breakingNews[currentIndex].cluster_headline}
                        </Text>
                    </div>
                    
                    {breakingNews[currentIndex].cluster_summary && (
                        <div 
                            style={{ 
                                marginBottom: '12px',
                                fontSize: '14px',
                                color: isDark ? '#d9d9d9' : '#595959',
                                lineHeight: '1.5'
                            }}
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(
                                    breakingNews[currentIndex].cluster_summary.length > 300
                                        ? `${breakingNews[currentIndex].cluster_summary.substring(0, 300)}...`
                                        : breakingNews[currentIndex].cluster_summary
                                )
                            }}
                        />
                    )}
                    
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        fontSize: '12px'
                    }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <Tag 
                                color={isDark ? "gold" : "orange"}
                                style={{ margin: 0, fontSize: '11px' }}
                            >
                                ⚡{breakingNews[currentIndex].last_breaking_rating || breakingNews[currentIndex].root_breaking_rating}
                            </Tag>
                            <Text 
                                style={{ 
                                    color: isDark ? '#8c8c8c' : '#8c8c8c',
                                    fontSize: '11px'
                                }}
                            >
                                {breakingNews[currentIndex].last_breaking_update_datetime
                                    ? dayjs(breakingNews[currentIndex].last_breaking_update_datetime).fromNow()
                                    : 'Recently'}
                            </Text>
                        </div>
                        <Text 
                            style={{ 
                                color: isDark ? '#8c8c8c' : '#8c8c8c',
                                fontSize: '11px'
                            }}
                        >
                            Click to view details
                        </Text>
                    </div>
                </div>
            )}

            {/* CSS Animations */}
            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.7; }
                    }
                    
                    @keyframes scrollText {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                    
                    @keyframes slideIn {
                        0% { transform: translateX(100%); opacity: 0; }
                        100% { transform: translateX(0); opacity: 1; }
                    }
                    
                    @keyframes slideOut {
                        0% { transform: translateX(0); opacity: 1; }
                        100% { transform: translateX(-100%); opacity: 0; }
                    }
                    
                    @keyframes slideUp {
                        0% { transform: translateY(20px); opacity: 0; }
                        100% { transform: translateY(0); opacity: 1; }
                    }
                    
                    /* Styles for HTML content in popup */
                    .breaking-news-popup p {
                        margin: 8px 0;
                        line-height: 1.5;
                    }
                    
                    .breaking-news-popup strong, .breaking-news-popup b {
                        font-weight: 600;
                        color: ${isDark ? '#ffffff' : '#000000'};
                    }
                    
                    .breaking-news-popup em, .breaking-news-popup i {
                        font-style: italic;
                    }
                    
                    .breaking-news-popup ul, .breaking-news-popup ol {
                        margin: 8px 0;
                        padding-left: 20px;
                    }
                    
                    .breaking-news-popup li {
                        margin: 4px 0;
                    }
                    
                    .breaking-news-popup a {
                        color: ${isDark ? '#40a9ff' : '#1890ff'};
                        text-decoration: none;
                    }
                    
                    .breaking-news-popup a:hover {
                        text-decoration: underline;
                    }
                    
                    .breaking-news-popup blockquote {
                        margin: 8px 0;
                        padding: 8px 12px;
                        border-left: 3px solid ${isDark ? '#40a9ff' : '#1890ff'};
                        background: ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'};
                        font-style: italic;
                    }
                `}
            </style>
        </>
    );
};

export default BreakingNewsTicker;